import Phaser from 'phaser';
import {
  ClientMessage,
  costumeTint,
  isValidRoomCode,
  ServerMessage,
  type CreateRoomPayload,
  type ErrorPayload,
  type JoinAcceptedPayload,
  type JoinByCodePayload,
  type RoomCreatedPayload,
  type RoomErrorCode,
  type RoomListing,
  type RoomListPayload,
} from '@ponponland/shared';
import type { Room } from 'colyseus.js';
import { canOpenModal, escapeHtml, mountRoot, showToast } from '../ui/dom.js';
import { logoHtml, ponBlobHtml, starfieldHtml } from '../ui/blob.js';
import { session } from '../state/session.js';
import { rememberRoom } from '../state/persist.js';
import { joinGameByCode, joinLobby, leaveLobby, net } from '../net/client.js';

/**
 * Room Browser — faithful to the prototype `RoomBrowser` + `RoomCard`
 * (`docs/design/flow/screens1.jsx`) in its default `grid` layout. Slice 2 swaps
 * the slice-1 `MOCK_ROOMS` for the **live** `ServerMessage.RoomList` from the
 * Colyseus `LobbyRoom`: the same `RoomListing[]` shape, so the UI is unchanged.
 *
 * The scene joins the LobbyRoom on create, renders the synced list and re-paints
 * on every push, and wires the Create / Join-by-Code / card-Join paths to the
 * real protocol messages + `RoomCreated`/`JoinAccepted`/`Error` acks. On a
 * successful create/join it re-joins the `GameRoom` by code and enters the Lobby
 * scene; `Error` codes surface as friendly toasts.
 */
export class RoomBrowserScene extends Phaser.Scene {
  private root!: HTMLElement;
  private lobby: Room | null = null;
  private rooms: RoomListing[] = [];
  /** Guards against double-acting on a create/join while one is in flight. */
  private navigating = false;
  /**
   * Password the user submitted for the in-flight create/join, so the
   * post-ack `GameRoom` re-join (`enterRoom`) presents it to `onAuth`. The
   * lobby acks (`RoomCreated`/`JoinAccepted`) don't echo it back, and public
   * rooms leave it undefined.
   */
  private pendingPassword?: string;
  /** Set when the lobby connection fails, so the list shows a clear error
   * instead of sitting on "Connecting…" and the action buttons explain the
   * dead state rather than silently no-op'ing. */
  private connectFailed = false;

  constructor() {
    super('RoomBrowser');
  }

  create(): void {
    this.navigating = false;
    this.root = mountRoot(this, this.shellHtml());
    this.root.classList.add('pp-flat'); // square corners on this banded page (no rounded frame)
    this.renderList();

    this.root.querySelector<HTMLButtonElement>('#pp-back')!.addEventListener('click', () => {
      void leaveLobby();
      this.scene.start('Splash');
    });
    this.root.querySelector<HTMLButtonElement>('#pp-create')!.addEventListener('click', () =>
      this.openCreateModal()
    );
    this.root.querySelector<HTMLButtonElement>('#pp-joincode')!.addEventListener('click', () =>
      this.openJoinModal()
    );

    void this.connect();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private async connect(): Promise<void> {
    try {
      this.lobby = await joinLobby();
    } catch {
      this.connectFailed = true;
      const count = this.root.querySelector<HTMLElement>('#pp-count');
      if (count) count.textContent = "Can't reach the server — try again later";
      showToast(this.root, 'Could not reach the server — is it running?');
      return;
    }
    this.connectFailed = false;
    this.lobby.onMessage<RoomListPayload>(ServerMessage.RoomList, (payload) => {
      this.rooms = payload.rooms ?? [];
      this.renderList();
    });
    this.lobby.onMessage<RoomCreatedPayload>(ServerMessage.RoomCreated, (payload) => {
      void this.enterRoom(payload.code, payload.name);
    });
    this.lobby.onMessage<JoinAcceptedPayload>(ServerMessage.JoinAccepted, (payload) => {
      void this.enterRoom(payload.code, payload.name);
    });
    this.lobby.onMessage<ErrorPayload>(ServerMessage.Error, (payload) => {
      this.navigating = false;
      this.pendingPassword = undefined;
      showToast(this.root, this.errorToast(payload.code, payload.message));
    });
    this.lobby.send(ClientMessage.ListRooms);
  }

  /** Re-join the game room by code, leave the lobby, and open the Lobby scene. */
  private async enterRoom(code: string, name: string): Promise<void> {
    if (net.game) return;
    // Carry the password the user submitted at create/join through to the
    // GameRoom's `onAuth`, then clear it (single-use, never reused).
    const password = this.pendingPassword;
    this.pendingPassword = undefined;
    try {
      await joinGameByCode(code, session.player, password);
    } catch {
      this.navigating = false;
      showToast(this.root, 'Could not join that room.');
      return;
    }
    // Remember the room in the browser so a reload drops the player back into
    // this lobby (cleared on an explicit Leave). Carries the private-room
    // password so auto-rejoin works for locked rooms.
    rememberRoom(code, name, password);
    await leaveLobby();
    this.scene.start('Lobby');
  }

  private teardown(): void {
    this.lobby?.removeAllListeners();
    this.lobby = null;
  }

  // -- render ---------------------------------------------------------------

  private shellHtml(): string {
    const player = session.player;
    const avatar = ponBlobHtml({
      colour: costumeTint(player.costume),
      costume: player.costume,
      size: 22,
      eyes: 'happy',
    });
    return `<div class="scene fade-in">
        ${starfieldHtml(18)}
        <div class="topbar" style="border-bottom:1px solid var(--stroke-soft);z-index:2">
          <button class="btn ghost icon-btn" id="pp-back" aria-label="back">‹</button>
          ${logoHtml(0.5)}
          <h2 style="margin-left:4px">Find a Room</h2>
          <div style="flex:1"></div>
          <div class="chip" style="gap:6px"><span style="display:inline-flex;margin-left:-2px">${avatar}</span>${escapeHtml(player.name)}</div>
        </div>

        <div class="scroll" style="flex:1;padding:14px 18px;z-index:2;display:flex;flex-direction:column">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
            <div class="muted" id="pp-count" style="font-size:13px;font-weight:700">Connecting…</div>
            <div class="chip teal">● Online</div>
          </div>
          <!-- flex:1 so the list fills the panel height; cards pack to the top,
               the empty state stretches to fill (grid-template-rows toggled in renderList). -->
          <div id="pp-grid" style="flex:1;min-height:0;overflow:auto;display:grid;grid-template-columns:1fr 1fr;gap:12px;align-content:start"></div>
        </div>

        <div style="flex:none;padding:12px 18px;display:flex;gap:12px;z-index:2">
          <button class="btn teal lg" id="pp-create" style="flex:1">＋ Create Room</button>
          <button class="btn ghost lg" id="pp-joincode" style="flex:1">🔢 Join by Code</button>
        </div>
      </div>`;
  }

  private renderList(): void {
    const count = this.root.querySelector<HTMLElement>('#pp-count');
    const grid = this.root.querySelector<HTMLElement>('#pp-grid');
    if (!count || !grid) return;
    count.textContent = `${this.rooms.length} open ${this.rooms.length === 1 ? 'room' : 'rooms'}`;
    // Empty: one full-height row so the empty-state card fills the panel.
    // Populated: auto rows so cards keep their natural height, packed at the top.
    grid.style.gridTemplateRows = this.rooms.length ? '' : '1fr';
    grid.innerHTML = this.rooms.length
      ? this.rooms.map((r) => roomCardHtml(r)).join('')
      : emptyStateHtml();
    grid.querySelectorAll<HTMLButtonElement>('[data-join]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const code = btn.getAttribute('data-join')!;
        const room = this.rooms.find((r) => r.code === code);
        if (room?.private) {
          this.openJoinModal(code);
        } else {
          this.requestJoin(code);
        }
      });
    });
  }

  // -- requests -------------------------------------------------------------

  private requestJoin(code: string, password?: string): void {
    if (this.navigating) return;
    if (!this.lobby) {
      showToast(
        this.root,
        this.connectFailed
          ? "Can't reach the server — try again later."
          : 'Still connecting to the server — try again in a moment.'
      );
      return;
    }
    if (!isValidRoomCode(code)) {
      showToast(this.root, 'Enter a valid 4-digit room code.');
      return;
    }
    this.navigating = true;
    // Remember it for the post-ack GameRoom re-join (private-room joiner path).
    this.pendingPassword = password || undefined;
    const payload: JoinByCodePayload = { code, ...(password ? { password } : {}) };
    this.lobby.send(ClientMessage.JoinByCode, payload);
  }

  // -- modals ---------------------------------------------------------------

  /** Create Room modal — faithful to the prototype `CreateRoomModal`. */
  private openCreateModal(): void {
    if (!canOpenModal(this.root)) return;
    const player = session.player;
    const bg = document.createElement('div');
    bg.className = 'modal-bg';
    bg.innerHTML = `<div class="modal panel">
      <h3>Create a Room</h3>
      <div class="muted" style="font-size:13px;margin-bottom:14px">Host a plaza and invite friends with the code.</div>
      <div style="margin-bottom:14px">
        <label class="lab">Room name</label>
        <input class="field" id="cr-name" maxlength="20" />
      </div>
      <div style="margin-bottom:14px">
        <label class="lab">Game mode</label>
        <div style="display:flex;gap:8px">
          <button data-mode="coop" class="cr-mode" style="flex:1;text-align:left;cursor:pointer;border-radius:12px;padding:9px 12px;transition:all .15s">
            <div style="font-weight:700;font-size:14px;color:var(--ink)">🤝 Co-op</div>
            <div class="muted" style="font-size:11px">Solve puzzles together</div>
          </button>
          <button data-mode="pvp" class="cr-mode" style="flex:1;text-align:left;cursor:pointer;border-radius:12px;padding:9px 12px;transition:all .15s">
            <div style="font-weight:700;font-size:14px;color:var(--ink)">⚔️ PVP</div>
            <div class="muted" style="font-size:11px">Battle in many modes</div>
          </button>
        </div>
      </div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:18px">
        <div>
          <div style="font-weight:700;font-size:14px">Private room</div>
          <div class="muted" style="font-size:12px">Require a password to join</div>
        </div>
        <button id="cr-priv" aria-label="toggle private" style="width:52px;height:30px;border-radius:999px;border:none;cursor:pointer;position:relative;transition:.2s;box-shadow:inset 0 0 0 2px var(--stroke)">
          <span style="position:absolute;top:3px;width:24px;height:24px;border-radius:50%;background:#fff;transition:.2s"></span>
        </button>
      </div>
      <div id="cr-pwwrap" style="display:none;margin-bottom:18px">
        <label class="lab">Password</label>
        <input class="field" id="cr-pw" maxlength="16" placeholder="min 3 characters" />
      </div>
      <div style="display:flex;gap:10px">
        <button class="btn ghost" id="cr-cancel" style="flex:1">Cancel</button>
        <button class="btn teal" id="cr-create" style="flex:1.4">Create →</button>
      </div>
    </div>`;
    this.root.appendChild(bg);

    const modal = bg.querySelector<HTMLElement>('.modal')!;
    const nameInput = bg.querySelector<HTMLInputElement>('#cr-name')!;
    const pwWrap = bg.querySelector<HTMLElement>('#cr-pwwrap')!;
    const pwInput = bg.querySelector<HTMLInputElement>('#cr-pw')!;
    const privBtn = bg.querySelector<HTMLButtonElement>('#cr-priv')!;
    const privKnob = privBtn.querySelector<HTMLElement>('span')!;
    const createBtn = bg.querySelector<HTMLButtonElement>('#cr-create')!;
    const modeBtns = Array.from(bg.querySelectorAll<HTMLButtonElement>('.cr-mode'));

    nameInput.value = `${player.name}'s Room`;
    let mode: CreateRoomPayload['mode'] = 'coop';
    let priv = false;

    const paintModes = (): void => {
      for (const b of modeBtns) {
        const on = b.getAttribute('data-mode') === mode;
        b.style.background = on ? '#fff7e6' : 'var(--panel-lo)';
        b.style.border = on ? '2.5px solid var(--gold)' : '2px solid var(--stroke-soft)';
        b.style.boxShadow = on ? '0 0 12px -3px var(--gold)' : 'none';
      }
    };
    const paintPriv = (): void => {
      privBtn.style.background = priv ? 'var(--teal)' : 'var(--panel-lo)';
      privKnob.style.left = priv ? '25px' : '3px';
      pwWrap.style.display = priv ? 'block' : 'none';
    };
    const refresh = (): void => {
      createBtn.disabled = !(nameInput.value.trim().length >= 2 && (!priv || pwInput.value.length >= 3));
    };

    for (const b of modeBtns) {
      b.addEventListener('click', () => {
        mode = b.getAttribute('data-mode') === 'pvp' ? 'pvp' : 'coop';
        paintModes();
      });
    }
    privBtn.addEventListener('click', () => {
      priv = !priv;
      paintPriv();
      refresh();
    });
    nameInput.addEventListener('input', refresh);
    pwInput.addEventListener('input', refresh);

    const close = (): void => bg.remove();
    bg.addEventListener('click', close);
    modal.addEventListener('click', (e) => e.stopPropagation());
    bg.querySelector<HTMLButtonElement>('#cr-cancel')!.addEventListener('click', close);
    createBtn.addEventListener('click', () => {
      if (createBtn.disabled || this.navigating) return;
      if (!this.lobby) {
        showToast(
          this.root,
          this.connectFailed
            ? "Can't reach the server — try again later."
            : 'Still connecting to the server — try again in a moment.'
        );
        return;
      }
      this.navigating = true;
      // Remember it for the post-ack GameRoom re-join (private-room host path).
      this.pendingPassword = priv ? pwInput.value : undefined;
      const payload: CreateRoomPayload = {
        name: nameInput.value.trim(),
        private: priv,
        mode,
        ...(priv ? { password: pwInput.value } : {}),
      };
      close();
      this.lobby.send(ClientMessage.CreateRoom, payload);
    });

    paintModes();
    paintPriv();
    refresh();
  }

  /** Join by Code modal — faithful to the prototype `JoinByCodeModal`. */
  private openJoinModal(prefill?: string): void {
    if (!canOpenModal(this.root)) return;
    const isPrivate = prefill !== undefined;
    const bg = document.createElement('div');
    bg.className = 'modal-bg';
    const cell = (i: number): string =>
      `<input data-i="${i}" inputmode="numeric" maxlength="1" style="width:56px;height:68px;text-align:center;font-family:var(--display);font-weight:700;font-size:26px;color:#8a5a10;background:#fffdf4;border:2px solid var(--wood);border-radius:12px;outline:none" />`;
    bg.innerHTML = `<div class="modal panel" style="width:min(380px,84%);text-align:center">
      <h3>Join by Code</h3>
      <div class="muted" style="font-size:13px;margin-bottom:20px">Enter the 4-digit room code from your host.</div>
      <div style="display:flex;gap:12px;justify-content:center;margin-bottom:${isPrivate ? 16 : 22}px">${[0, 1, 2, 3].map(cell).join('')}</div>
      ${isPrivate ? `<div style="margin-bottom:18px;text-align:left"><label class="lab">Password</label><input class="field" id="jc-pw" maxlength="16" placeholder="room password" /></div>` : ''}
      <div style="display:flex;gap:10px">
        <button class="btn ghost" id="jc-cancel" style="flex:1">Cancel</button>
        <button class="btn" id="jc-join" style="flex:1.4">Join Room →</button>
      </div>
    </div>`;
    this.root.appendChild(bg);

    const modal = bg.querySelector<HTMLElement>('.modal')!;
    const cells = Array.from(bg.querySelectorAll<HTMLInputElement>('[data-i]'));
    const pwInput = bg.querySelector<HTMLInputElement>('#jc-pw');
    const joinBtn = bg.querySelector<HTMLButtonElement>('#jc-join')!;

    const code = (): string => cells.map((c) => c.value).join('');
    const refresh = (): void => {
      joinBtn.disabled = !isValidRoomCode(code());
    };

    if (prefill) {
      prefill.split('').forEach((d, i) => {
        if (cells[i]) cells[i].value = d;
      });
    }

    cells.forEach((c, i) => {
      c.addEventListener('input', () => {
        c.value = c.value.replace(/[^0-9]/g, '').slice(0, 1);
        if (c.value && i < 3) cells[i + 1].focus();
        refresh();
      });
      c.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && !c.value && i > 0) cells[i - 1].focus();
      });
      c.addEventListener('focus', () => (c.style.borderColor = 'var(--teal)'));
      c.addEventListener('blur', () => (c.style.borderColor = 'var(--wood)'));
    });

    const close = (): void => bg.remove();
    bg.addEventListener('click', close);
    modal.addEventListener('click', (e) => e.stopPropagation());
    bg.querySelector<HTMLButtonElement>('#jc-cancel')!.addEventListener('click', close);
    joinBtn.addEventListener('click', () => {
      if (joinBtn.disabled) return;
      const c = code();
      close();
      this.requestJoin(c, pwInput?.value || undefined);
    });

    refresh();
    (prefill && pwInput ? pwInput : cells[0]).focus();
  }

  // -- copy -----------------------------------------------------------------

  private errorToast(code: RoomErrorCode, fallback: string): string {
    switch (code) {
      case 'room-not-found':
        return 'Room not found.';
      case 'room-full':
        return 'Room is full.';
      case 'room-in-progress':
        return 'That game has already started.';
      case 'wrong-password':
        return 'Wrong password.';
      case 'invalid-code':
        return 'Enter a valid 4-digit room code.';
      default:
        return fallback || 'Could not join that room.';
    }
  }
}

/** One room card — faithful to the prototype `RoomCard` (grid layout). */
function roomCardHtml(room: RoomListing): string {
  const full = room.players >= room.max;
  const dots = Array.from({ length: room.max })
    .map(
      (_, i) =>
        `<span style="width:13px;height:13px;border-radius:50%;background:${i < room.players ? 'var(--teal)' : 'var(--panel-lo)'};border:1.5px solid var(--stroke)"></span>`
    )
    .join('');
  const joinBtn = full
    ? `<button class="btn ghost sm" disabled>Full</button>`
    : `<button class="btn sm" data-join="${escapeHtml(room.code)}">Join</button>`;
  return `<div class="panel panel-hi" style="padding:12px 14px;display:flex;align-items:center;gap:12px;border-radius:16px;opacity:${full ? 0.6 : 1}">
    <div style="flex:1;min-width:0">
      <div style="display:flex;align-items:center;gap:7px">
        <span style="font-size:15px;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(room.name)}</span>
        ${room.private ? '<span title="private" style="font-size:12px">🔒</span>' : ''}
      </div>
      <div class="muted" style="font-size:12px;margin-top:2px">Host: ${escapeHtml(room.host)} · ${escapeHtml(room.stage)}</div>
      <div style="display:flex;gap:4px;margin-top:7px;align-items:center">
        ${dots}
        <span class="muted" style="font-size:12px;margin-left:4px;font-weight:600">${room.players}/${room.max}</span>
      </div>
    </div>
    ${joinBtn}
  </div>`;
}

/** Friendly empty state when no rooms are open (criterion #6). */
function emptyStateHtml(): string {
  return `<div class="panel" style="grid-column:1 / -1;height:100%;box-sizing:border-box;padding:26px 18px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center">
    <div style="font-size:30px;margin-bottom:6px">🫧</div>
    <div style="font-weight:800;font-size:15px;color:var(--ink)">No open rooms yet</div>
    <div class="muted" style="font-size:13px;margin-top:4px">Create one to start a plaza, or join with a 4-digit code.</div>
  </div>`;
}
